import { useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 7 — Highest Note Inversion Exercise.
 *
 * For every melody note that walks up the scale, pick the chord (I, IV or V)
 * that contains it and the INVERSION that puts that note on top. End up
 * with a chord-and-melody texture where the highest voice is the tune.
 *
 * Bespoke visual: a Scale-Ladder map. Eight clickable scale degrees on top.
 * Pick a degree → it picks the chord (I/IV/V) that contains it AND the
 * inversion that puts that degree as the top voice. Audible per click.
 */

interface LadderRow {
  /** Scale degree of the melody note (1..8 — 1 is tonic, 8 is octave). */
  degree: number;
  /** Roman numeral of the chosen chord. */
  chord: "I" | "IV" | "V";
  /** Inversion number (0=root, 1=1st, 2=2nd) chosen so the top voice is `degree`. */
  inversion: 0 | 1 | 2;
  /** The three pitch-class offsets from tonic, bottom to top, voiced from the bass register. */
  offsets: [number, number, number];
}

// In a major key, what's at the top of each chord-degree pairing:
// I   = 1-3-5;  scale degrees 1,3,5 land here
// IV  = 4-6-1;  scale degrees 4,6,8 land here
// V   = 5-7-2;  scale degrees 5,7,9 land here
// We pick the most musical default for each note:
const LADDER: LadderRow[] = [
  { degree: 1, chord: "I",  inversion: 1, offsets: [4, 7, 12] },   // 3-5-1: top is 1
  { degree: 2, chord: "V",  inversion: 1, offsets: [11, 14, 17] }, // 7-2-4: top is 2 (V root pos with octave-2)
  { degree: 3, chord: "I",  inversion: 2, offsets: [7, 12, 16] },  // 5-1-3: top is 3
  { degree: 4, chord: "IV", inversion: 0, offsets: [5, 9, 12] },   // 4-6-1: top is 4 — wait top is 1. Let's re-think:
];
// We'll regenerate LADDER inline below for correctness — keeping the type only.

export default function PianoIntermediate7() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "7");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="G" availableQualities={["major", "harmonic-minor"]}>
        <PIShell
          title="Highest Note Inversion Exercise"
          exerciseGoal="Walk up the scale. Each note becomes the top voice of a chord. Pick the chord-and-inversion combination that lifts your melody."
          bpm={72}
          difficulty="Advanced"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

/* For each scale degree, pick the chord (from {I, IV, V}) that contains it
 * AND the inversion whose top voice IS that degree. There are several valid
 * choices per degree; we use the choice Jason walks through in the
 * transcript: keep things to I/IV/V, prefer the chord that gives a smooth
 * voice-leading climb. */
function computeLadder(
  pcFromDegree: (d: number) => number
): {
  degree: number;
  chord: "I" | "IV" | "V";
  notes: { pc: number; isTop: boolean; degree: number }[];
}[] {
  // Each row: top voice = scale degree d; the inversion is built so that
  // the top of the stacked triad equals the wanted scale degree.
  const rows: { degree: number; chord: "I" | "IV" | "V"; degTriad: [number, number, number] }[] = [
    { degree: 1, chord: "I",  degTriad: [3, 5, 1] }, // 1st inv of I: 3-5-1, top=1 ✓
    { degree: 2, chord: "V",  degTriad: [5, 7, 2] }, // root pos of V: 5-7-2, top=2 ✓
    { degree: 3, chord: "I",  degTriad: [5, 1, 3] }, // 2nd inv of I: 5-1-3, top=3 ✓
    { degree: 4, chord: "IV", degTriad: [6, 1, 4] }, // 1st inv of IV: 6-1-4, top=4 ✓
    { degree: 5, chord: "I",  degTriad: [1, 3, 5] }, // root of I: top=5 ✓
    { degree: 6, chord: "IV", degTriad: [1, 4, 6] }, // 2nd inv of IV: 1-4-6, top=6 ✓
    { degree: 7, chord: "V",  degTriad: [2, 5, 7] }, // 2nd inv of V: 2-5-7, top=7 ✓
    { degree: 8, chord: "I",  degTriad: [3, 5, 1] }, // 1st inv of I, octave up — top=1 (=8) ✓
  ];
  return rows.map((r) => ({
    degree: r.degree,
    chord: r.chord,
    notes: r.degTriad.map((deg, i) => ({
      pc: pcFromDegree(deg),
      isTop: i === r.degTriad.length - 1,
      degree: deg,
    })),
  }));
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const ladder = computeLadder(k.pcFromDegree);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  const playRow = async (idx: number) => {
    const row = ladder[idx];
    kickAudio();
    setActiveIdx(idx);
    // Build chord MIDI numbers in a comfortable register: bass voice ≥ tonic-1 octave, top voice ≈ scale degree near melody register
    const noteMidis = row.notes.map((n) => {
      const semis = ((n.pc - k.tonicPc) % 12 + 12) % 12;
      // Stack from a starting bass register, pushing each voice above the previous
      return tonicMidi - 12 + semis;
    });
    // Force ascending order
    for (let i = 1; i < noteMidis.length; i++) {
      while (noteMidis[i] <= noteMidis[i - 1]) noteMidis[i] += 12;
    }
    await playSequence(
      [{ time: 0, notes: noteMidis.map((m) => midiToPitchName(m, k.useFlats)), duration: "2n" }],
      72
    );
  };

  // Master keyboard: light up the I/IV/V root + the active row's top voice
  const masterHighlights: KeyHighlight[] = [
    { degree: 1, tone: "root", label: "I" },
    { degree: 4, tone: "voice", label: "IV" },
    { degree: 5, tone: "voice", label: "V" },
  ];

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={masterHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="The three chords of the lesson — I, IV, V"
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          The three chords are <strong>I, IV, V</strong> in the current key.
          Every step of the ladder below picks one of these and stacks it so
          the melody note sits on top.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="Scale climbing, chord-by-chord">
          <p>
            Walk up the scale in your right hand, but make every note the
            <em> top voice</em> of a triad. Each scale degree has a "right"
            chord — the one from <strong>I</strong>, <strong>IV</strong> or
            <strong> V</strong> that contains it — and a "right" inversion —
            the one whose top voice IS that scale degree.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Every single note has a chord which can work with it and those
            chords are 1, 4, 5 just to make things easy on the mind and on
            the fingers."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The ladder" title="Click any scale degree to hear it">
          <p>
            Below is the ladder for <strong>{k.tonicName} {k.quality === "major" ? "major" : "harmonic minor"}</strong>.
            Each row shows the melody note (top), the chord that contains it,
            and the three voices stacked from bass to top. The amber voice is
            the melody. Click a row to hear it. On the 5th of the scale
            Jason offers either chord: the I (root position) or the V with
            its root on top.
          </p>
          <ScaleInversionLadder
            rows={ladder}
            activeIdx={activeIdx}
            onPlay={playRow}
            nameForPc={k.nameForPc}
            minor={k.quality !== "major"}
          />
          <PIInsight>
            <p>
              <strong>Notice the pattern:</strong> degrees 1, 3, 5 are I-chord
              tones (so they need different inversions of I). Degrees 4, 6, 8
              all live in IV. Degrees 2, 5, 7 all live in V. The ladder
              alternates between three chord families because three chords
              cover every note of a major scale.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Bass awareness" title="Always play the TRUE root in the bass">
          <p>
            Even though the right hand is using inversions, the left hand
            should always play the <strong>true root</strong> of whichever
            chord is sounding. Otherwise you accidentally write a slash chord:
            <em> I/3</em>, <em>IV/6</em>, etc. Sometimes that's beautiful;
            today we keep it disciplined and play the root.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Whether you play G major like this, like this or like this, you
            would be playing G always in our bass."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="The dressed-up version" title="High–Low–Middle–Low arpeggio">
          <p>
            Once the block-chord version is clean, switch the right hand to
            an arpeggio: <strong>High → Low → Middle → Low</strong>. The
            highest finger (typically the pinky) holds the melody note. The
            other voices ripple under it.
          </p>
          <p>
            It sounds a lot better than the block chords, and it's Jason's
            pick for an arpeggio pattern you can use for most music.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Finger flipping" title="Don't commit one finger to one note">
          <p>
            The orthodox advice "always use the same finger for the same key"
            doesn't survive this exercise. As you climb, you'll need to
            <strong> swap fingers mid-key</strong> — substitute the ring for
            the pinky on a held note so your hand can keep climbing. It looks
            ugly. It sounds great.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "Some people could call it ugly, I just call it efficient, right?
            And music is for the ear, it's not for the eyes."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="The whole exercise in your current key">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="7"
            label={`Highest note inversion · ${k.tonicName}`}
            originalBpm={72}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={
              <>
                Don't try all twelve keys at once. Get one key really clean,
                then move on. Jason's advice: not one and not twelve — somewhere
                in between.
              </>
            }
            rungs={[
              { title: "Block chords ascending", minutes: 4, accent: "amber",   detail: "Walk up the scale in the right hand. Each note = chord top. LH plays the true root. Block chords, no arpeggio yet." },
              { title: "Block chords descending", minutes: 4, accent: "cyan",   detail: "Same idea, walking down. Watch your finger flips on the way back." },
              { title: "H-L-M-L arpeggio",        minutes: 4, accent: "rose",   detail: "Same chord choices, but the right hand arpeggiates: highest, lowest, middle, lowest. The highest finger HOLDS through every cycle." },
              { title: "Two new keys",             minutes: 4, accent: "emerald",detail: "Pick two keys you don't know well — perhaps A♭ major and B major — and run the whole ladder twice in each." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: scale-degree → chord-inversion ladder ── */
function ScaleInversionLadder({
  rows,
  activeIdx,
  onPlay,
  nameForPc,
  minor = false,
}: {
  rows: ReturnType<typeof computeLadder>;
  activeIdx: number | null;
  onPlay: (idx: number) => void;
  nameForPc: (pc: number) => string;
  /** Harmonic minor: show i and iv in lower case (V stays major). */
  minor?: boolean;
}) {
  const chordTone: Record<"I" | "IV" | "V", { ring: string; chip: string; bar: string }> = {
    I:  { ring: "ring-amber-300/60",   chip: "bg-amber-500/20 text-amber-200",   bar: "from-amber-500 to-amber-300" },
    IV: { ring: "ring-cyan-300/60",    chip: "bg-cyan-500/20 text-cyan-200",     bar: "from-cyan-500 to-cyan-300" },
    V:  { ring: "ring-rose-300/60",    chip: "bg-rose-500/20 text-rose-200",     bar: "from-rose-500 to-rose-300" },
  };
  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-3">
      <div className="space-y-1.5">
        {rows.map((row, idx) => {
          const ch = chordTone[row.chord];
          const isActive = idx === activeIdx;
          return (
            <motion.button
              key={idx}
              layout
              onClick={() => onPlay(idx)}
              className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors ${
                isActive
                  ? `border-amber-300 bg-amber-500/10 ring-2 ${ch.ring}`
                  : "border-cyan-400/20 bg-zinc-900/30 hover:bg-cyan-500/5"
              }`}
            >
              {/* Scale-degree label */}
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-zinc-950/60 font-mono text-sm font-bold text-cyan-100">
                {row.degree}
              </div>

              {/* Chord chip */}
              <div className={`rounded-md px-2 py-0.5 font-mono text-[11px] font-bold ${ch.chip}`}>
                {minor && row.chord !== "V" ? row.chord.toLowerCase() : row.chord}
              </div>

              {/* Voice stack — three pills, top one amber (= melody note) */}
              <div className="flex flex-1 flex-col-reverse gap-1">
                {row.notes.map((n, i) => (
                  <span
                    key={i}
                    className={`inline-flex h-5 items-center justify-center rounded px-2 text-[11px] font-semibold ${
                      n.isTop
                        ? "bg-amber-400 text-zinc-900"
                        : "bg-zinc-800 text-cyan-200"
                    }`}
                  >
                    {nameForPc(n.pc)} <span className="ml-1 opacity-60">· deg&nbsp;{n.degree}</span>
                  </span>
                ))}
              </div>

              <div className="text-[11px] uppercase tracking-wider text-cyan-300/70">
                ▶
              </div>
            </motion.button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] italic text-cyan-300/70">
        Amber pill = the top voice (your melody note). The chord's other
        voices stack below it.
      </p>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
